<script setup lang="ts">
import Button from '@clovyr/bed/components/controls/Button.vue';
import Copy from '@clovyr/bed/components/elements/Copy.vue';
import { type Deployment, DeploymentState } from '@clovyr/pollen';
import type { Manifest } from '@clovyr/pollen/manifest';
import { type LocalSsoAccess, type LocalSsoInvite, UserAPI } from '@clovyr/pollen/user';

import Module from '@/components/elements/Module.vue';
import { useAppVersion } from '@/composables/useAppVersion';
import { config as appConfig } from '@/init/config';
import { addLocalSsoValues, type LocalSsoMember } from '@/stores/local_sso';
import { usePollenStore } from '@/stores/pollen_store';

const props = defineProps<{
  deployment: Deployment;
  app: Manifest;
}>();

const pollenStore = usePollenStore();
const invites = ref<LocalSsoInvite[]>([]);
const inviteURL = ref('');
const loading = ref(false);
const error = ref('');
const fetching = ref(false);
const loaded = ref(false);
let loadSequence = 0;

const activeInvites = computed(() => invites.value.filter((invite) => !invite.revokedAt));

function inviteMembers(items: LocalSsoInvite[]): LocalSsoMember[] {
  return items.flatMap((invite) => {
    if (!invite.recipient || invite.revokedAt || !invite.redeemedAt) {
      return [];
    }
    return [
      {
        inviteID: invite.id,
        subject: invite.recipient.subject,
        role: 'default',
        passkeys: invite.recipient.passkeys,
      },
    ];
  });
}

async function loadInvites(): Promise<LocalSsoAccess> {
  const sequence = ++loadSequence;
  const fqdn = props.deployment.fqdn;
  loaded.value = false;
  fetching.value = false;
  if (!fqdn) {
    throw new Error('missing AI Cloud FQDN');
  }
  fetching.value = true;
  try {
    const access = await new UserAPI().getLocalSsoAccess(fqdn);
    if (sequence !== loadSequence || fqdn !== props.deployment.fqdn) {
      throw new Error('deployment changed while loading access');
    }
    invites.value = access.invites;
    loaded.value = true;
    return access;
  } finally {
    if (sequence === loadSequence) fetching.value = false;
  }
}

async function applyGrants() {
  const deployment = props.deployment;
  const access = await loadInvites();
  if (!Array.isArray(access.administrators)) throw new Error('missing administrator metadata');
  const app = await pollenStore.garden.loadDeploymentDetails(props.app);
  if (deployment !== props.deployment) throw new Error('deployment changed while loading access');
  const audience = deployment.appSettings.CLOVYR_PUBLIC_URL;
  if (typeof audience !== 'string') {
    throw new Error('missing AI Cloud public URL');
  }
  addLocalSsoValues(
    deployment.appSettings,
    deployment.id,
    audience,
    appConfig.VITE_PUBLIC_URL,
    [],
    inviteMembers(access.invites),
    access.administrators,
  );
  await pollenStore.pollen.putDeployment(deployment);
  await useAppVersion(deployment.libraryItemID ?? deployment.id).runAppVersion(app);
}

async function createInvite() {
  loading.value = true;
  error.value = '';
  try {
    if (!props.deployment.fqdn) {
      throw new Error('missing AI Cloud FQDN');
    }
    inviteURL.value = await pollenStore.pollen.createAICloudInvite(
      props.deployment.libraryItemID ?? props.deployment.id,
    );
    await loadInvites();
  } catch {
    error.value = 'Unable to create an invite.';
  } finally {
    loading.value = false;
  }
}

async function revokeInvite(invite: LocalSsoInvite) {
  loading.value = true;
  error.value = '';
  try {
    await new UserAPI().deleteLocalSsoInvite(invite.id);
    await loadInvites();
    if (invite.redeemedAt) {
      await applyGrants();
    }
  } catch {
    error.value = 'Unable to revoke access.';
  } finally {
    loading.value = false;
  }
}

async function refreshPasskeys() {
  loading.value = true;
  error.value = '';
  try {
    await applyGrants();
  } catch {
    error.value = 'Unable to refresh app access.';
  } finally {
    loading.value = false;
  }
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
}

async function reloadAccess() {
  invites.value = [];
  error.value = '';
  const sequence = loadSequence + 1;
  try {
    await loadInvites();
  } catch {
    if (sequence === loadSequence) {
      error.value =
        'Unable to load app access. Check your connection and owner or editor instance access.';
    }
  }
}

watch(
  () => [props.deployment.libraryItemID, props.deployment.fqdn, props.deployment.state],
  () => {
    inviteURL.value = '';
    void reloadAccess();
  },
  { immediate: true },
);
</script>

<template>
  <Module class="ai-cloud-access">
    <h3>Private app access</h3>
    <p>
      Invite another Clovyr account to add AI Cloud to their library. Their passkey stays on their
      device.
    </p>
    <div class="ai-cloud-access__actions">
      <Button
        size="small"
        :disabled="loading || fetching || !loaded"
        label="Create invite"
        @click="createInvite"
      />
      <Button
        size="small"
        :disabled="loading || fetching || !loaded || deployment.state !== DeploymentState.Running"
        label="Refresh passkeys"
        @click="refreshPasskeys"
      />
      <Copy v-if="inviteURL" label="Copy invite" :text="inviteURL" />
    </div>
    <p v-if="error" role="alert">{{ error }}</p>
    <Button
      v-if="error && !loaded"
      size="small"
      :disabled="fetching"
      label="Retry"
      @click="reloadAccess"
    />
    <p v-if="fetching">Loading app access…</p>
    <div v-else-if="loaded && activeInvites.length" class="ai-cloud-access__table-wrap">
      <table class="ai-cloud-access__table">
        <thead>
          <tr>
            <th scope="col">Account</th>
            <th scope="col">Status</th>
            <th scope="col">Expires</th>
            <th scope="col" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invite in activeInvites" :key="invite.id">
            <td>{{ invite.recipient?.subject || 'Awaiting acceptance' }}</td>
            <td>
              <span
                class="ai-cloud-access__status"
                :class="{ 'ai-cloud-access__status--active': invite.redeemedAt }"
              >
                {{ invite.redeemedAt ? 'Active' : 'Pending' }}
              </span>
            </td>
            <td>
              <time :datetime="invite.expiresAt">{{ formatDate(invite.expiresAt) }}</time>
            </td>
            <td class="ai-cloud-access__revoke">
              <Button
                size="small"
                label="Revoke"
                :disabled="loading || fetching || deployment.state !== DeploymentState.Running"
                @click="revokeInvite(invite)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else-if="loaded && !error" class="ai-cloud-access__empty">No active invitations.</p>
  </Module>
</template>

<style scoped lang="scss">
.ai-cloud-access {
  padding: 1.5rem;

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1.25rem 0;

    :deep(.button) {
      margin: 0;
    }

    :deep(.copy-btn) {
      height: calc(var(--space-relative) * 3);
      min-height: 0;
      padding: 0.35rem 0.8rem;
      font-size: 0.75rem;
    }
  }

  &__table-wrap {
    margin-top: 1.5rem;
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;

    th,
    td {
      padding: 0.85rem 0.75rem;
      border-bottom: 1px solid var(--collection-text-translucent);
      vertical-align: middle;
    }

    th {
      font-size: 0.75rem;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    td:first-child {
      font-weight: 600;
    }
  }

  &__status {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--collection-text-translucent);
    font-size: 0.75rem;

    &--active {
      background: var(--collection-text-secondary);
    }
  }

  &__revoke {
    width: 1%;
    white-space: nowrap;

    :deep(.button) {
      margin: 0;
    }
  }

  &__empty {
    margin: 1.5rem 0 0;
  }
}
</style>
